import { getBlockDefinition } from './blockCatalog';
import {
  BRANCH_ACTION_LABELS,
  parseIfElseConfig,
  type AutomationBranchAction,
} from './ifElseConfig';
import { describeSchedule, parseScheduleConfig } from './scheduleConfig';
import { getLinearActionNodes } from './workflowGraph';
import type {
  AutomationActionType,
  AutomationExecutionEntry,
  AutomationGraph,
  AutomationGraphNode,
  AutomationTrigger,
} from '../types/AutomationWorkflow.types';
import { getActionLabel } from '../types/AutomationWorkflow.types';

export type PlainEnglishSegment = {
  text: string;
  highlight?: boolean;
};

export const formatRelativeTime = (
  value: Date | string | null | undefined,
): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 15) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const formatRelativeSavedTime = (
  value: Date | string | null | undefined,
): string => {
  const relative = formatRelativeTime(value);
  return relative ? `Saved ${relative}` : 'Not saved yet';
};

const describeBranchPlain = (branch: AutomationBranchAction): string => {
  switch (branch.type) {
    case 'add_to_serato_crate': {
      const crate = String(branch.config.crateName ?? '').trim();
      return crate
        ? `drop it in the Serato crate ${crate}`
        : 'add it to a Serato crate';
    }
    case 'copy_move_file': {
      const folder = String(branch.config.destinationFolder ?? '').trim();
      const mode = String(branch.config.mode ?? 'copy');
      return folder
        ? `${mode === 'move' ? 'move' : 'copy'} it into ${folder}`
        : mode === 'move'
          ? 'move the file'
          : 'copy the file';
    }
    case 'import_to_library':
      return 'index it in my library';
    case 'read_write_tags':
      return 'update its tags';
    default:
      return getActionLabel(branch.type).toLowerCase();
  }
};

const describeActionPlain = (node: AutomationGraphNode): string => {
  switch (node.type as AutomationActionType) {
    case 'import_to_library':
      return 'index it in my library';
    case 'read_write_tags': {
      const mode = String(node.config.mode ?? 'read');
      if (mode === 'read') return 'read its tags';
      const grouping = String(
        node.config.grouping ?? node.config.genre ?? '',
      ).trim();
      if (grouping) return `tag it ${grouping}`;
      return 'update its tags';
    }
    case 'add_to_serato_crate': {
      const crate = String(node.config.crateName ?? '').trim();
      return crate
        ? `add it to the Serato crate ${crate}`
        : 'add it to a Serato crate';
    }
    case 'copy_move_file': {
      const mode = String(node.config.mode ?? 'copy');
      const folder = String(node.config.destinationFolder ?? '').trim();
      if (!folder) {
        return mode === 'move' ? 'move the file' : 'copy the file';
      }
      return mode === 'move'
        ? `move it into ${folder}`
        : `copy it into ${folder}`;
    }
    case 'if_else': {
      const config = parseIfElseConfig(node.config);
      return `drop it in the Serato crate that matches its BPM, otherwise ${describeBranchPlain(config.falseBranch)}`;
    }
    case 'run_agent':
      return 'ask Crate Hackers to handle it';
    default:
      return getActionLabel(node.type as AutomationActionType).toLowerCase();
  }
};

export const buildPlainEnglishSegments = (
  trigger: AutomationTrigger,
  graph: AutomationGraph,
): PlainEnglishSegment[] => {
  const actions = getLinearActionNodes(graph);
  const segments: PlainEnglishSegment[] = [];

  if (trigger.type === 'file_added') {
    const folder = String(trigger.config.folderPath ?? '').trim();
    segments.push({ text: 'When a file is added to ' });
    segments.push({
      text: folder || 'your watch folder',
      highlight: Boolean(folder),
    });
  } else if (trigger.type === 'schedule') {
    segments.push({ text: 'On ' });
    segments.push({
      text: describeSchedule(parseScheduleConfig(trigger.config)).toLowerCase(),
      highlight: true,
    });
  } else {
    segments.push({ text: 'When you run this workflow manually' });
  }

  if (actions.length === 0) {
    segments.push({
      text: ', add steps below to finish building this automation.',
    });
    return segments;
  }

  actions.forEach((node, index) => {
    segments.push({ text: index === 0 ? ', ' : ', then ' });
    appendHighlightedActionSegments(node, segments);
  });
  segments.push({ text: '.' });
  return segments;
};

const appendHighlightedActionSegments = (
  node: AutomationGraphNode,
  segments: PlainEnglishSegment[],
): void => {
  switch (node.type as AutomationActionType) {
    case 'read_write_tags': {
      const grouping = String(
        node.config.grouping ?? node.config.genre ?? '',
      ).trim();
      if (grouping) {
        segments.push({ text: 'tag it ' });
        segments.push({ text: grouping, highlight: true });
        return;
      }
      break;
    }
    case 'add_to_serato_crate': {
      const crate = String(node.config.crateName ?? '').trim();
      if (crate) {
        segments.push({ text: 'add it to the Serato crate ' });
        segments.push({ text: crate, highlight: true });
        return;
      }
      break;
    }
    case 'copy_move_file': {
      const folder = String(node.config.destinationFolder ?? '').trim();
      if (folder) {
        const mode = String(node.config.mode ?? 'copy');
        segments.push({
          text: `${mode === 'move' ? 'move' : 'copy'} it into `,
        });
        segments.push({ text: folder, highlight: true });
        return;
      }
      break;
    }
    case 'if_else': {
      const config = parseIfElseConfig(node.config);
      segments.push({
        text: 'drop it in the Serato crate that matches its BPM',
      });
      const falseCrate = String(
        config.falseBranch.config.crateName ?? '',
      ).trim();
      if (falseCrate) {
        segments.push({ text: ', otherwise ' });
        segments.push({ text: falseCrate, highlight: true });
      }
      return;
    }
    default:
      break;
  }

  segments.push({ text: describeActionPlain(node) });
};

export const buildPlainEnglishSummary = (
  trigger: AutomationTrigger,
  graph: AutomationGraph,
): string =>
  buildPlainEnglishSegments(trigger, graph)
    .map((segment) => segment.text)
    .join('');

export const getBranchStatsFromLog = (
  nodeId: string,
  executionLog: AutomationExecutionEntry[],
): { trueCount: number; falseCount: number; total: number } => {
  let trueCount = 0;
  let falseCount = 0;
  executionLog.forEach((entry) => {
    const branch = entry.branchTaken?.[nodeId];
    if (branch === 'true') trueCount += 1;
    if (branch === 'false') falseCount += 1;
  });
  return { trueCount, falseCount, total: trueCount + falseCount };
};

export const getActionSubtitle = (node: AutomationGraphNode): string => {
  const definition = getBlockDefinition(node.type as AutomationActionType);
  if (!definition) return '';

  switch (node.type as AutomationActionType) {
    case 'read_write_tags':
      if (String(node.config.mode ?? 'read') === 'write') {
        const grouping = String(node.config.grouping ?? '').trim();
        return grouping
          ? `Write Grouping = '${grouping}'`
          : 'Write tags on the incoming file';
      }
      return definition.description;
    case 'import_to_library':
      return 'Index the file in My Library';
    case 'if_else':
      return 'Split on BPM';
    case 'add_to_serato_crate': {
      const crate = String(node.config.crateName ?? '').trim();
      return crate ? `Drop matching files in ${crate}` : definition.description;
    }
    case 'copy_move_file': {
      const folder = String(node.config.destinationFolder ?? '').trim();
      const mode = String(node.config.mode ?? 'copy');
      return folder
        ? `${mode === 'move' ? 'Move' : 'Copy'} files to ${folder}`
        : definition.description;
    }
    default:
      return definition.description;
  }
};

export const getActionConfigChips = (
  node: AutomationGraphNode,
): { label: string; value: string }[] => {
  switch (node.type as AutomationActionType) {
    case 'import_to_library':
      return [
        ...(node.config.analyzeBpmKey !== false
          ? [{ label: 'Option', value: 'Analyze BPM & key' }]
          : []),
        ...(node.config.flagDuplicates !== false
          ? [{ label: 'Option', value: 'Flag duplicates' }]
          : []),
      ];
    case 'read_write_tags':
      if (String(node.config.mode ?? 'read') !== 'write') return [];
      return ['title', 'artist', 'album', 'genre', 'grouping', 'comment']
        .map((key) => ({
          label: key.charAt(0).toUpperCase() + key.slice(1),
          value: String(node.config[key] ?? '').trim(),
        }))
        .filter((chip) => chip.value);
    case 'add_to_serato_crate': {
      const crate = String(node.config.crateName ?? '').trim();
      return crate ? [{ label: 'Crate', value: crate }] : [];
    }
    case 'copy_move_file': {
      const folder = String(node.config.destinationFolder ?? '').trim();
      const mode = String(node.config.mode ?? 'copy');
      return folder
        ? [{ label: mode === 'move' ? 'Move to' : 'Copy to', value: folder }]
        : [];
    }
    case 'run_agent': {
      const instructions = String(node.config.instructions ?? '').trim();
      return instructions
        ? [{ label: 'Prompt', value: instructions.slice(0, 80) }]
        : [];
    }
    default:
      return [];
  }
};

export const getTriggerStepLabel = (trigger: AutomationTrigger): string => {
  if (trigger.type === 'file_added') return 'A file is added';
  if (trigger.type === 'schedule') return 'The schedule comes around';
  return 'I run it manually';
};

export const parseExtensionList = (raw: unknown): string[] =>
  String(raw ?? 'mp3, wav, flac, aiff, m4a')
    .split(',')
    .map((item) => item.trim().replace(/^\./, '').toLowerCase())
    .filter(Boolean);

export const formatExtensionValue = (extensions: string[]): string =>
  extensions.join(', ');

export { BRANCH_ACTION_LABELS };
